import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import ReplacementAPI from 'api/faber/ReplacementAPI'
import WinbackAPI from 'api/faber/WinbackAPI'
import DemoDatePicker from 'common/AbstractSingleDatePicker'
import { RoundedButton } from 'common/Button/Button'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import TextColorFontHeader from 'common/Typography/TextColorFontHeader'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

import { PaperComponent } from './UpdateFaberUserDetailsDialog'

function EsimRequestDialog({
  handleRequestEsimDialogClose,
  requestEsimDialog,
  customerRow,
  faberToken,
  actionEvent
}: RequestDialogProps) {
  const [remarks, setRemarks] = useState<string>('')
  const [disable, setDisable] = useState<boolean>(true)

  /*HANDLE FORM INPUT */
  const handleEsimRequestRemarks = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    evt.preventDefault()
    const newValue: string = evt.target.value
    setRemarks(newValue)
    if (evt.target.value.length < 1) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }
  /*API CALLS */
  /*TO POST ADDITIONAL DATA*/
  const { mutate: updateBonusDataPlans } = useMutation(
    WinbackAPI.updateBonusDataPlans
  )

  function addAdditionalDataConfirm() {
    updateBonusDataPlans({
      userID: customerRow.userID,
      packageTypeID: '315390',
      remarks: 'Add extra 20gb for Pro Pac user: Esim Replacement',
      faberToken: faberToken
    })
  }
  /*TO POST ESIM REPLACMENT*/
  const { mutate: mutationReplaceEsim, isLoading: isReplacingLoading } =
    useMutation(ReplacementAPI.esimReplacement, {
      onSuccess: () => {
        toast.success('New eSim has been requested for selected customer')
        if (
          Number(customerRow.cycleCount) === 1 &&
          customerRow.planName === 'Pro Pac'
        ) {
          addAdditionalDataConfirm()
        }
      },
      onError: () => {
        toast.error('Error requesting for new esim')
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    })

  const { mutate: resendEsimEmail, isLoading: isEmailSendingLoading } =
    useMutation(EsimAPI.resendEsimEmail, {
      onSuccess: () => {
        toast.success('Resent esim subscription email')
      },
      onError: () => {
        toast.error('Error resending email')
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    })
  const handleConfirmRequest = () => {
    if (actionEvent === 'Replace eSIM') {
      mutationReplaceEsim({
        userId: customerRow.userID,
        remarks: remarks,
        faberToken: faberToken
      })
    } else
      resendEsimEmail({
        userId: customerRow.userID,
        faberToken: faberToken
      })
  }
  return (
    <Dialog
      open={requestEsimDialog}
      onClose={() => {
        handleRequestEsimDialogClose()
      }}
      fullWidth={true}
      maxWidth='md'
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: '-15px',
            mt: '-5px'
          }}
        >
          <TextColorFontHeader>{actionEvent}</TextColorFontHeader>
          <CrossDialogBtn dialogClose={handleRequestEsimDialogClose} />
        </Box>
      </DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent>
        {actionEvent === 'Replace eSIM' ? (
          <TextColorFontBody sx={{ fontSize: '16px' }}>
            This action will replace customer&apos;s current eSIM and resend the
            new eSIM activation email to customer.
          </TextColorFontBody>
        ) : (
          <TextColorFontBody sx={{ fontSize: '16px' }}>
            This action will resend the eSIM activation email to customer.
          </TextColorFontBody>
        )}
        <TextColorFontBody sx={{ fontSize: '16px' }}>
          Are you sure you want to proceed with this action?
        </TextColorFontBody>
        <TextColorFontBody variant='subtitle1' sx={{ fontSize: '12px' }}>
          (After clicking the confirm button, the action is irreversible)
        </TextColorFontBody>
        {actionEvent === 'Replace eSIM' ? (
          <Box
            component='form'
            sx={{
              rowGap: '1em',
              columnGap: '1em',
              gridTemplateColumns: 'repeat(3, 1fr)',
              mt: '20px'
            }}
            display='grid'
            noValidate
            autoComplete='off'
          >
            <DemoDatePicker label='Date' disabled={true} />

            <TextField
              id='Requested By'
              label='Requested By'
              defaultValue={customerRow.userEmail}
              variant='standard'
              disabled
            />
            <TextField
              required
              id='Remarks'
              label='Remarks'
              defaultValue=''
              variant='standard'
              onChange={handleEsimRequestRemarks}
              error={!remarks}
              helperText={!remarks && 'This is a required field'}
            />
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <RoundedButton
          variant='outlined'
          onClick={() => handleRequestEsimDialogClose()}
        >
          Cancel
        </RoundedButton>
        <LoadingButton
          loading={isReplacingLoading || isEmailSendingLoading}
          loadingIndicator={<CircularProgress size={20} />}
          variant='filled'
          disabled={actionEvent === 'Replace eSIM' && disable}
          onClick={handleConfirmRequest}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
      {/* </ColorBox> */}
    </Dialog>
  )
}

export default EsimRequestDialog
interface RequestDialogProps {
  handleRequestEsimDialogClose: () => void
  requestEsimDialog: boolean
  customerRow: SubscriptionsTableRows
  faberToken: string
  actionEvent: string
}
